import React, {useEffect, useMemo, useState} from 'react'
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import {SaveInventoryItem} from '../../utils'
import {CachedProduct, getCachedProducts} from '../../productCache'
import {ExcludedDetailsDialog} from '../../../common/ui/ExcludedDetailsDialog'

interface CountIterationReportProps {
    serialItems: SaveInventoryItem[]
    nonSerialItems: SaveInventoryItem[]
}

const CountIterationReport = ({
                                  serialItems,
                                  nonSerialItems
                              }: CountIterationReportProps) => {
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [selectedItem, setSelectedItem] = useState<SaveInventoryItem | null>(null)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const fetchProducts = async () => {
            const cachedProducts = await getCachedProducts()
            setProducts(Object.values(cachedProducts))
        }

        fetchProducts()
    }, [])

    const getModelNumber = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product ? product.model : 'Unknown Model'
    }

    const serialCount = useMemo(() => {
        return serialItems.filter(item => !item.excluded).length
    }, [serialItems])

    const nonSerialCount = useMemo(() => {
        return nonSerialItems.reduce((count, item) => count + item.quantity, 0)
    }, [nonSerialItems])

    const excludedCount = useMemo(() => {
        return serialItems.filter(item => item.excluded).length
    }, [serialItems])

    const reportSerialItems = [...serialItems].reverse()
    const reportNonSerialItems = [...nonSerialItems]

    const handleClickOpen = (item: SaveInventoryItem) => {
        setSelectedItem(item)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setSelectedItem(null)
    }

    const handleCopy = (str: string) => {
        navigator.clipboard.writeText(str)
            .then(() => {
                alert('Copied to clipboard!')
            })
            .catch(err => {
                console.error('Failed to copy: ', err)
            })
    }

    return (
        <Box p={4}>
            <Typography variant="h5" gutterBottom>
                Count Iteration Report
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
                <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                {serialCount === 1 ? `${serialCount} serial` : `${serialCount} serials`},{' '}
                {nonSerialCount === 1 ? `${nonSerialCount} non-serial` : `${nonSerialCount} non-serials`},{' '}
                <span style={{color: 'red'}}>{excludedCount} excluded </span>
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={4}>
                <Box flex={1} mr={2}>
                    <Typography variant="h6" gutterBottom>
                        Serials
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    style={{
                                        borderBottom:
                                            reportSerialItems.length > 0 &&
                                            reportSerialItems[0].excluded
                                                ? 'none'
                                                : '2px solid #ddd'
                                    }}>
                                    <TableCell
                                        sx={{display: 'none'}}></TableCell>
                                    <TableCell>Serial</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Excluded</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportSerialItems.map(item => (
                                    <TableRow key={item.serial}
                                              sx={{
                                                  border: item.excluded ? '2px solid red' : 'none',
                                                  color: item.excluded ? 'red' : 'inherit'
                                              }}>
                                        <TableCell
                                            sx={{display: 'none'}}></TableCell>
                                        <TableCell>{item.serial}</TableCell>
                                        <TableCell>
                                            {getModelNumber(item.sku)}
                                        </TableCell>
                                        <TableCell>
                                            {item.excluded && (
                                                <Tooltip title={<h1 style={{
                                                    color: 'text.primary',
                                                    fontSize: 15,
                                                    fontWeight: 'medium'
                                                }}>{item.excluded.startsWith('not-found:') ? 'not-found' : item.excluded}</h1>}>
                                                    <Box
                                                        fontSize="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: 'red'
                                                        }}
                                                        onClick={() => handleClickOpen(item)}>
                                                        {item.excluded.split(':')[0]}
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {selectedItem && (
                    <ExcludedDetailsDialog
                        open={open}
                        selectedItem={selectedItem}
                        onClose={handleClose}
                        onCopy={handleCopy}
                    />
                )}

                <Box flex={1} ml={2}>
                    <Typography variant="h6" gutterBottom>
                        Non-Serials
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{display: 'none'}}></TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Quantity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportNonSerialItems.map(item => (
                                    <TableRow key={item.sku}>
                                        <TableCell
                                            sx={{display: 'none'}}></TableCell>
                                        <TableCell>{item.sku}</TableCell>
                                        <TableCell>
                                            {getModelNumber(item.sku)}
                                        </TableCell>
                                        <TableCell>{item.quantity}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    )
}

export default CountIterationReport
